import { Component, type ErrorInfo, type ReactNode } from 'react';

/**
 * Catches a render error so one broken component does not blank the whole app.
 *
 * Without this, any exception thrown while rendering unmounts the entire tree
 * and leaves a white page with nothing to click: no message, no way back, and
 * nothing in the UI to say what happened. A visitor's only option is to guess
 * that reloading might help.
 *
 * The boundary sits inside the router, so the header and the tab bar survive and
 * the visitor can navigate away from whichever page failed.
 */
interface Props { children: ReactNode }
interface State { error: Error | null }

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // The browser console is the only place this is recorded; the page itself
    // never shows a stack trace to a visitor.
    console.error('render failed', error, info.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <section className="section">
        <div className="container max-w-[52ch]">
          <p className="kicker">Something broke</p>
          <h1 className="h2 mt-1">This page did not load.</h1>
          <p className="body-muted mt-3">
            The rest of Kova is still working. Try this page again, or head back to the scoreboard.
          </p>
          <div className="mt-5 flex flex-wrap gap-2">
            <button onClick={() => this.setState({ error: null })} className="btn-primary">Try again</button>
            <a href="/" className="btn border border-line">Back to scoreboard</a>
          </div>
        </div>
      </section>
    );
  }
}
